/**
 * PATH: packages/ui/src/kanban/auditRevert.ts
 * FILE_ID: UI-KANBAN-AUDIT-REVERT-001
 * PURPOSE: Map a single Card.audit_log_json entry → a reversible plan
 *          that the FE can execute by calling existing KanbanApi
 *          mutations, with no new backend endpoint required.
 * WHY:
 *   Operator request 2026-05-15: *"Add the ability to undo last
 *   action.. this should be part of the tracing activity, with just
 *   revert and it reverts that action.."*
 *
 *   The kanban CardDetailDrawer already renders the audit trail
 *   (Activity panel). Three events captured today record BOTH the
 *   before-value and the after-value, so the inverse is deterministic
 *   from the row alone:
 *     1. `lane_changed`     → moveCard back to `from_lane`/`from_position`
 *     2. `card_reordered`   → moveCard within the same lane to `from_position`
 *     3. `card_assignees_set` → assignCard with `user_ids: before`
 *
 *   Eight additional single-field event kinds (status/priority/dates/
 *   note/blocked/sensitive/snooze/pinned) are reversible via
 *   `updateCard(id, { <field>: before })`. Those cases live in
 *   `audit-revert/planForFieldEntry.ts`; this orchestrator handles the
 *   move/reorder/assignees cases and composes the field mapper.
 *
 *   Other events (action_invoked, card_restricted_to_set, merges,
 *   backfills) either lose the before-value or have side effects we
 *   can't safely auto-rewind. They're surfaced as NOT reversible
 *   with an explanation rather than silently hidden so the operator
 *   understands why the row has no Revert button.
 *
 * FLOW:
 *   ┌──────────────┐    ┌──────────────────────────┐    ┌──────────────┐
 *   │ audit entry  │ →  │ getCardAuditRevertPlan() │ →  │  inverse API │
 *   │ + card + api │    │  (pure I→O, no IO)       │    │   mutation   │
 *   └──────────────┘    └──────────────────────────┘    └──────────────┘
 *
 *   The function itself performs no IO — it returns a plan object
 *   whose `execute()` is called by the host (the drawer's Revert
 *   button) when the operator clicks. Keeps the policy decision
 *   ("is this reversible?") in one testable place and the side
 *   effect at the click site.
 *
 * DEPENDENCIES:
 *   - ./api: KanbanApi (only `moveCard` + `assignCard` used here)
 *   - ./types: AuditLogEntry, Card
 *   - ./audit-revert/*: plan type, before-value readers, field-case mapper
 *
 * RELATED:
 *   - CardDetailDrawer.tsx Activity section — UI consumer
 *   - email_kanban/views/cards.py (cards endpoint) — the writer
 *     of these audit rows. Any new reversible event added there
 *     should also gain a branch here or in planForFieldEntry.ts.
 */

import type { AuditLogEntry, Card } from './types'
import type { CardAuditRevertPlan, GetCardAuditRevertPlanInput } from './audit-revert/types'
import { _readNumber, _readNumberArray, _readString } from './audit-revert/readers'
import { planForFieldEntry } from './audit-revert/planForFieldEntry'

export type { CardAuditRevertPlan, GetCardAuditRevertPlanInput } from './audit-revert/types'
export { _MISSING } from './audit-revert/readers'

/**
 * Pure mapping: audit entry → revert plan. No IO, no React, no
 * mutable state — safe to import from anywhere and test in
 * isolation.
 */
export function getCardAuditRevertPlan(
  input: GetCardAuditRevertPlanInput,
): CardAuditRevertPlan {
  const { entry, card, api, resolveLaneName } = input

  const laneLabel = (laneId: string): string => {
    const name = resolveLaneName?.(laneId)
    return name ? `"${name}"` : `lane ${laneId.slice(0, 8)}`
  }

  switch (entry.event) {
    case 'lane_changed': {
      const fromLane = _readString(entry.from_lane)
      const fromPosition = _readNumber(entry.from_position)
      if (!fromLane) {
        return {
          canRevert: false,
          reason: 'Audit row missing from_lane — cannot determine where to move back to.',
        }
      }
      if (fromPosition === null) {
        // We can still move back to the from_lane even without a
        // position — the backend will append to the lane. The
        // operator may want this; surface it as reversible but say
        // "position will be at end".
      }
      return {
        canRevert: true,
        label: 'Revert move',
        describe: `Move this card back to ${laneLabel(fromLane)}${
          fromPosition !== null ? ' at its previous position.' : '.'
        }`,
        execute: async () => {
          return await api.moveCard(card.id, {
            lane_id: fromLane,
            ...(fromPosition !== null ? { position: fromPosition } : {}),
            reason: 'Reverted from activity log',
          })
        },
      }
    }

    case 'card_reordered': {
      const fromPosition = _readNumber(entry.from_position)
      if (fromPosition === null) {
        return {
          canRevert: false,
          reason: 'Audit row missing from_position — cannot reorder back.',
        }
      }
      // Reorder stays on the same lane. Prefer `entry.from_lane` if
      // present (matches the BE writer), fall back to the card's
      // current lane.
      const sameLane = _readString(entry.from_lane) ?? card.lane
      return {
        canRevert: true,
        label: 'Revert reorder',
        describe: 'Move this card back to its previous position in this list.',
        execute: async () => {
          return await api.moveCard(card.id, {
            lane_id: sameLane,
            position: fromPosition,
            reason: 'Reverted from activity log',
          })
        },
      }
    }

    case 'card_assignees_set': {
      const before = _readNumberArray(entry.before)
      if (before === null) {
        return {
          canRevert: false,
          reason: 'Audit row missing before-assignees — cannot restore them.',
        }
      }
      if (!api.assignCard) {
        return {
          canRevert: false,
          reason: 'Assignment endpoint not wired by this host.',
        }
      }
      const assignCard = api.assignCard
      return {
        canRevert: true,
        label: 'Revert assignees',
        describe:
          before.length === 0
            ? 'Clear current assignees (the card had none before this action).'
            : `Restore the previous ${before.length} assignee${
                before.length === 1 ? '' : 's'
              }.`,
        execute: async () => {
          return await assignCard(card.id, { user_ids: before })
        },
      }
    }

    default: {
      // Single-field reversible events (status/priority/dates/note/
      // blocked/sensitive/snooze/pinned) — mapped in one place.
      const fieldPlan = planForFieldEntry({ entry, card, api })
      if (fieldPlan) return fieldPlan
      return {
        canRevert: false,
        reason: `"${entry.event}" actions are not reversible from the activity log.`,
      }
    }
  }
}

/**
 * UAT P1-c — newest-first scan over a card's audit trail for the most
 * recent entry that yields a reversible plan. Powers the right-click
 * "Revert last action" menu entry; the drawer's Activity tab remains
 * the per-row surface for older history.
 *
 * Pure I→O like the per-entry function: no IO here, the caller owns
 * fetching the FULL card first (board lists use the lean projection
 * which drops `audit_log_json` — an empty/missing trail simply
 * returns null and the menu entry hides).
 */
export function getLatestRevertablePlan(input: {
  card: Pick<Card, 'id' | 'lane'> & { audit_log_json?: AuditLogEntry[] | null }
  api: GetCardAuditRevertPlanInput['api']
  resolveLaneName?: GetCardAuditRevertPlanInput['resolveLaneName']
}): { entry: AuditLogEntry; plan: Extract<CardAuditRevertPlan, { canRevert: true }> } | null {
  const trail = input.card.audit_log_json
  if (!Array.isArray(trail) || trail.length === 0) return null
  for (let i = trail.length - 1; i >= 0; i--) {
    const entry = trail[i]
    if (!entry || typeof entry !== 'object') continue
    const plan = getCardAuditRevertPlan({
      entry,
      card: input.card,
      api: input.api,
      resolveLaneName: input.resolveLaneName,
    })
    if (plan.canRevert) return { entry, plan }
  }
  return null
}
